import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import './PaymentSuccess.css';
import { API_URL } from '../config/api';


const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  /**
   * Esta pantalla atiende dos pagos distintos que no se parecen en nada.
   *
   * Uno es la suscripción: la paga el dueño del negocio, que está logueado, y
   * abajo se verifica con su token. El otro es la seña de un turno: la paga un
   * cliente cualquiera desde la página pública del negocio, y NO está logueado.
   *
   * Con eso alcanzaba para romperlo: el camino de abajo arranca con
   * `currentUser.getIdToken()` y sólo corre `if (currentUser)`, así que quien
   * pagaba una seña volvía de Mercado Pago a un "Verificando tu pago…" que no
   * terminaba nunca. Había pagado, tenía el turno, y la pantalla no se lo decía.
   *
   * El código del turno viene en la URL (lo arma el backend al generar el link)
   * y se consulta por el endpoint público, sin sesión.
   */
  const codigoDeTurno = searchParams.get('codigo');
  const negocioDelTurno = searchParams.get('negocio');
  const [turno, setTurno] = useState(null);
  const [buscandoTurno, setBuscandoTurno] = useState(Boolean(codigoDeTurno));

  useEffect(() => {
    if (!codigoDeTurno) return;

    const buscarTurno = async () => {
      try {
        const respuesta = await fetch(
          `${API_URL}/api/bookings/by-code/${codigoDeTurno.toUpperCase()}?businessId=${negocioDelTurno || ''}`
        );
        const datos = await respuesta.json();
        if (respuesta.ok) setTurno(datos.booking || datos);
      } catch (e) {
        console.error('No se pudo leer el turno recién pagado:', e);
      } finally {
        setBuscandoTurno(false);
      }
    };

    buscarTurno();
  }, [codigoDeTurno, negocioDelTurno]);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Check if this is a subscription payment
        const type = searchParams.get('type');
        const preapprovalId = searchParams.get('preapproval_id');
        const paymentId = searchParams.get('payment_id');
        const status = searchParams.get('status');
        const preferenceId = searchParams.get('preference_id');
        
        // For subscriptions, status might be 'authorized' instead of 'approved'
        if (status && status !== 'approved' && status !== 'authorized') {
          setError('El pago no fue aprobado');
          setVerifying(false);
          return;
        }

        const token = await currentUser.getIdToken();
        
        // If it's a subscription, verify the preapproval
        if (type === 'subscription' || preapprovalId) {
          console.log('Verifying subscription with preapproval ID:', preapprovalId);
          
          const response = await fetch(`${API_URL}/api/mercadopago/verify-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ 
              preapprovalId: preapprovalId,
              paymentId: paymentId 
            }),
          });

          const data = await response.json();
          
          if (data.success) {
            setVerified(true);
            setPlanInfo({
              planId: data.planId,
              planName: data.planName,
              billingCycle: data.billingCycle
            });
            
            // Actualizar localStorage para que el dashboard sepa que hay que refrescar
            localStorage.setItem('planUpdated', 'true');
            localStorage.setItem('subscriptionActivated', 'true');
          } else {
            setError(data.message || 'Error al verificar la suscripción');
          }
        } else if (paymentId) {
          // Legacy payment verification
          const response = await fetch(`${API_URL}/api/mercadopago/verify-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ paymentId }),
          });

          const data = await response.json();
          
          if (data.success) {
            setVerified(true);
            setPlanInfo({
              planId: data.planId,
              billingCycle: data.billingCycle
            });
            
            // Actualizar localStorage para que el dashboard sepa que hay que refrescar
            localStorage.setItem('planUpdated', 'true');
          } else {
            setError(data.message || 'Error al verificar el pago');
          }
        } else {
          setError('No se encontró información del pago');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('Error al verificar el pago. Por favor contacta soporte.');
      } finally {
        setVerifying(false);
      }
    };

    if (currentUser) {
      verifyPayment();
    }
  }, [currentUser, searchParams]);

  const handleContinue = () => {
    navigate('/admin/dashboard');
  };

  // ===== La vuelta del pago de una seña =====
  if (codigoDeTurno) {
    if (buscandoTurno) {
      return (
        <div className="payment-success-container">
          <div className="payment-success-card">
            <Loader2 className="payment-icon spinning" />
            <h1>Confirmando tu turno…</h1>
            <p>Un momento, estamos registrando tu pago.</p>
          </div>
        </div>
      );
    }

    // El aviso de Mercado Pago (el webhook) puede llegar unos segundos después
    // que la persona. No es un error y no hay que asustarla: el pago ya salió
    // de su cuenta y el horario le sigue reservado.
    const confirmado = turno?.status === 'confirmed';

    return (
      <div className="payment-success-container">
        <div className="payment-success-card">
          <CheckCircle className="payment-icon success" />
          <h1>{confirmado ? '¡Listo, tu turno está confirmado!' : 'Recibimos tu pago'}</h1>
          {!confirmado && (
            <p>Estamos terminando de confirmarlo. Puede tardar unos segundos.</p>
          )}
          {turno && (
            <div className="plan-info">
              {turno.service?.name && (
                <p className="plan-detail">Servicio: <strong>{turno.service.name}</strong></p>
              )}
              {turno.date && (
                <p className="plan-detail">Fecha: <strong>{turno.date}</strong></p>
              )}
              {turno.time && (
                <p className="plan-detail">Hora: <strong>{turno.time} hs</strong></p>
              )}
              <p className="plan-detail">
                Tu código: <strong>{turno.code || codigoDeTurno.toUpperCase()}</strong>
              </p>
            </div>
          )}
          <p className="next-steps">
            Guardá tu código: con él podés consultar o cancelar el turno.
          </p>
          <Button onClick={() => navigate('/')} size="lg" className="continue-button">
            Volver al inicio
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card">
          <Loader2 className="payment-icon spinning" />
          <h1>Verificando tu pago...</h1>
          <p>Por favor espera mientras confirmamos tu suscripción.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card error">
          <div className="error-icon">⚠️</div>
          <h1>Hubo un problema</h1>
          <p>{error}</p>
          <div className="button-group">
            <Button onClick={() => navigate('/admin/plans')} variant="outline">
              Volver a Planes
            </Button>
            <Button onClick={() => window.location.href = 'mailto:soporte@aturno.com'}>
              Contactar Soporte
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <div className="payment-success-card">
        <CheckCircle className="payment-icon success" />
        <h1>¡Pago exitoso!</h1>
        <p>Tu suscripción ha sido activada correctamente.</p>
        {planInfo && (
          <div className="plan-info">
            <p className="plan-detail">
              Plan activado: <strong>{planInfo.planId}</strong>
            </p>
            <p className="plan-detail">
              Ciclo de facturación: <strong>{planInfo.billingCycle === 'monthly' ? 'Mensual' : 'Anual'}</strong>
            </p>
          </div>
        )}
        <p className="next-steps">
          Ahora puedes disfrutar de todas las funcionalidades de tu plan.
        </p>
        <Button onClick={handleContinue} size="lg" className="continue-button">
          Ir al Dashboard
          <ArrowRight className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccess;